import React, {useCallback, useState} from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useSourcePackageBuilderStore from '../../../../stores/useSourcePackageBuilderStore';
import {getScoreColor, getScoreLabel} from '../../../../utils/claimAnalysisApi';
import {getProvenanceFromDocSourceType} from '../../../../features/products/reportEditorPage/leftPanel/components/sourcesProvenance';
import SourceProvenanceChip from '../../../segmentItem/SourceProvenanceChip';
import {isAboveU} from '../../helpers/documentClassification';
import {useDocumentClassificationCache} from '../../../../hooks/useDocumentClassificationCache';
import AlternativeCitationSuggestion, {FindAlternativeBadge} from './AlternativeCitationSuggestion';

// Static sx objects hoisted to module scope
const LIST_ITEM_SX = {
    bgcolor: 'rgba(0,0,0,0.02)',
    borderRadius: 1,
    p: 1.5,
    pr: 0.5,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {bgcolor: 'rgba(0,0,0,0.06)'},
};
const CONTENT_BOX_SX = {flex: 1, minWidth: 0};
const TITLE_SX = {fontWeight: 600, lineHeight: 1.4, mb: 0.5, wordBreak: 'break-word'};
const CHIPS_ROW_SX = {display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap'};
const FILE_TYPE_CHIP_SX = {height: 18, fontSize: '0.65rem', fontWeight: 500};
const SOURCE_CAPTION_SX = {color: 'text.secondary', display: 'block', mb: 0.5};
const CONTENT_SNIPPET_SX = {
    color: 'text.secondary',
    fontSize: '0.75rem',
    lineHeight: 1.4,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
};
const DELETE_BTN_SX = {width: 24, height: 24, padding: 0, flexShrink: 0, mt: 0.25};

/** Map documentType to a short display label. */
function getFileTypeLabel(documentType) {
    switch ((documentType || '').toUpperCase()) {
        case 'PDF':    return 'PDF';
        case 'AUDIO':  return 'Audio';
        case 'VIDEO':  return 'Video';
        case 'IMAGE':  return 'Image';
        case 'HTML':   return 'Web';
        case 'DOCX':
        case 'DOC':    return 'Word';
        case 'XLSX':
        case 'XLS':    return 'Excel';
        case 'PPTX':
        case 'PPT':    return 'Slides';
        default:       return documentType ? documentType.toUpperCase() : null;
    }
}

/** Map documentType to the fileType string expected by the Sources file viewer. */
function documentTypeToFileType(documentType) {
    switch ((documentType || '').toUpperCase()) {
        case 'PDF':   return 'pdf';
        case 'AUDIO': return 'audio';
        case 'VIDEO': return 'video';
        case 'IMAGE': return 'image';
        default:      return 'unknown';
    }
}

/**
 * Extract a usable file ID from the localId for legacy MY_DATA citations that
 * were stored before the fileId field was added.
 *
 * Data-item localIds use the format: d:{fileKey}:{pmDocumentId}:{pmFrom}
 * Report localIds use the format:    r:{reportId}:{segmentId|chunkIndex}
 */
function extractIdFromLocalId(localId, kind) {
    if (!localId || typeof localId !== 'string') return null;
    if (kind === 'file') {
        // d:{fileKey}:{pmDocumentId}:{pmFrom}
        const m = localId.match(/^d:([^:]+):/);
        return m ? m[1] : null;
    }
    if (kind === 'report') {
        // r:{reportId}:{segmentOrChunk}
        const m = localId.match(/^r:([^:]+):/);
        return m ? m[1] : null;
    }
    return null;
}

/**
 * Individual reference list item component
 * @param {Object} props
 * @param {Object} props.reference - The reference data
 * @param {number} props.citationNumber - The citation number for this reference
 * @param {Object} props.evidenceScore - Evidence score object with {score: number, reason: string}
 * @param {Object} props.cfg - Theme configuration
 * @param {Function} props.onDelete - Callback to delete this reference
 * @param {string} [props.reportId] - Report ID, needed to search for (U) alternatives
 * @param {string} [props.statementText] - Statement text this reference supports
 * @param {Function} [props.onReplaceWithAlternative] - (oldLocalId, suggestedSegment) => void
 * @param {Function} [props.onAddAlternativeAlongside] - (suggestedSegment) => void
 * @returns {JSX.Element}
 */
export default React.memo(function ReferenceListItem({
    reference,
    citationNumber,
    evidenceScore,
    cfg,
    onDelete,
    reportId,
    statementText,
    onReplaceWithAlternative,
    onAddAlternativeAlongside,
}) {
    const openSourcePackageBuilder = useSourcePackageBuilderStore(s => s.open);
    const setBuilderDocument = useSourcePackageBuilderStore(s => s.setDocument);
    const isBuilderOpen = useSourcePackageBuilderStore(s => s.isOpen);
    const [showAlternatives, setShowAlternatives] = useState(false);

    // MINSKY-17131: backfill classification for references added before docClassificationLevel
    // was persisted — skipped entirely (no fetch) when the reference already carries it.
    const {level: cachedLevel} = useDocumentClassificationCache(
        reference.docClassificationLevel ? null : reference.docId
    );
    const resolvedClassificationLevel = reference.docClassificationLevel ?? cachedLevel;
    // MINSKY-17131 fix: docId is intentionally null for non-corpus citations (My Data /
    // Report / Highlight), so it must not gate the badge — those citations still carry a
    // valid docClassificationLevel and AlternativeCitationSuggestion keys off localId.
    const showAboveUBadge = isAboveU(resolvedClassificationLevel);

    const handleOpenDocument = useCallback((e) => {
        if (e.target.closest('button')) return;

        try {
            const sourceType = (reference.docSourceType || '').toUpperCase();
            let openArgs;

            if (sourceType === 'MY_DATA' || sourceType === 'FILE'
                || sourceType === 'MY FILE' || sourceType === 'DATA_ITEM_REFERENCE'
                || sourceType === 'SOURCE-FILE' || sourceType === 'HIGHLIGHT') {
                // Priority: stored fileId → stored docId → parse from localId (legacy)
                const fileId = reference.fileId
                    ? String(reference.fileId)
                    : (reference.docId
                        ? String(reference.docId)
                        : extractIdFromLocalId(reference.localId, 'file'));
                if (!fileId) return;
                openArgs = {
                    viewKind: 'file',
                    filePayload: {
                        fileId,
                        fileName: reference.docTitle || null,
                        fileType: documentTypeToFileType(reference.documentType),
                        fileSource: 'my',
                        sentenceHit: null,
                    },
                };
            } else if (sourceType === 'REPORT' || sourceType === 'REPORTS' || sourceType === 'REPORT_SEGMENT') {
                // Priority: stored reportId → stored docId → parse from localId (legacy)
                const reportId = reference.reportId
                    ? String(reference.reportId)
                    : (reference.docId
                        ? String(reference.docId)
                        : extractIdFromLocalId(reference.localId, 'report'));
                if (!reportId) return;
                openArgs = {
                    viewKind: 'report',
                    reportPayload: {
                        reportId,
                        title: reference.docTitle || null,
                        chunkIndex: reference.index ?? null,
                    },
                };
            } else {
                const documentId = reference.docId ? String(reference.docId) : null;
                if (!documentId) return;
                openArgs = {
                    viewKind: 'corpus',
                    documentId,
                    segmentIndex: reference.index ?? null,
                };
            }

            if (isBuilderOpen) {
                setBuilderDocument(openArgs);
            } else {
                openSourcePackageBuilder(openArgs);
            }
        } catch (error) {
            console.error('Failed to open document:', error);
        }
    }, [reference, isBuilderOpen, setBuilderDocument, openSourcePackageBuilder]);

    const handleDelete = useCallback((e) => {
        e.stopPropagation();
        onDelete(reference.localId);
    }, [onDelete, reference.localId]);

    const provenance = getProvenanceFromDocSourceType(reference.docSourceType);
    const fileTypeLabel = getFileTypeLabel(reference.documentType);

    return (
        <Box>
            <ListItem
                onClick={handleOpenDocument}
                sx={LIST_ITEM_SX}
            >
                {/* Reference content */}
                <Box sx={CONTENT_BOX_SX}>

                    {/* Title — full wrap so long filenames are fully visible */}
                    <Typography
                        variant="subtitle2"
                        sx={TITLE_SX}
                    >
                        [{citationNumber}] {reference.docTitle || 'Reference'}
                    </Typography>

                    {/* Chips row: source type + file type + evidence score */}
                    <Box sx={CHIPS_ROW_SX}>
                        <SourceProvenanceChip provenance={provenance}/>
                        {fileTypeLabel ? (
                            <Chip
                                label={fileTypeLabel}
                                size="small"
                                variant="outlined"
                                sx={FILE_TYPE_CHIP_SX}
                            />
                        ) : null}
                        {(evidenceScore && evidenceScore.score != null) ? (
                            <Tooltip title={evidenceScore.reason || 'Evidence score'} arrow>
                                <Chip
                                    label={getScoreLabel(evidenceScore.score)}
                                    size="small"
                                    sx={{
                                        height: 18,
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        bgcolor: getScoreColor(evidenceScore.score),
                                        color: 'white',
                                        '&:hover': {bgcolor: getScoreColor(evidenceScore.score)},
                                    }}
                                />
                            </Tooltip>
                        ) : null}
                        {showAboveUBadge ? (
                            <FindAlternativeBadge
                                level={resolvedClassificationLevel}
                                expanded={showAlternatives}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowAlternatives((prev) => !prev);
                                }}
                            />
                        ) : null}
                    </Box>

                    {/* Source / domain */}
                    {reference.docSource ? (
                        <Typography
                            variant="caption"
                            sx={SOURCE_CAPTION_SX}
                        >
                            {reference.docSource}
                        </Typography>
                    ) : null}

                    {/* Content snippet — up to 3 lines */}
                    {reference.content ? (
                        <Typography
                            variant="body2"
                            sx={CONTENT_SNIPPET_SX}
                        >
                            {reference.content}
                        </Typography>
                    ) : null}
                </Box>

                {/* Delete button */}
                <IconButton
                    size="small"
                    onClick={handleDelete}
                    sx={DELETE_BTN_SX}
                >
                    <DeleteOutlineIcon fontSize="small"/>
                </IconButton>
            </ListItem>

            {showAboveUBadge && showAlternatives ? (
                <AlternativeCitationSuggestion
                    reference={{...reference, resolvedClassificationLevel}}
                    reportId={reportId}
                    statementText={statementText}
                    onReplace={(oldLocalId, segment) => onReplaceWithAlternative?.(oldLocalId, segment)}
                    onAddAlongside={(segment) => onAddAlternativeAlongside?.(segment)}
                />
            ) : null}
        </Box>
    );
});
